import React from "react";
import { styled } from "@mui/material/styles";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import Input from "../../../component-lib/Atoms/Input/Input";
import Button from "../../../component-lib/Molecules/button/Button";
import MultiSelect from "../../../component-lib/Atoms/MultiSelect/MultiSelect";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: "0px 24px 16px 24px",
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
  "& .MuiPaper-root": {
    borderRadius: "8px",
    width: "600px",
    maxWidth: "none",
  },
}));

const FIELD = {
  NAME: "name",
  IS_ITEM_REQUIRED: "isItemRequired",
  IS_NOTE_REQUIRED: "isNoteRequired",
  FACILITY_IDS: "facilityIds",
};

interface OrderTypeDialogProps {
  isOpen: boolean;
  handleClose: () => void;
  handleData: (data: {
    name: string;
    isItemRequired: boolean;
    isNoteRequired: boolean;
    facilityIds: string[];
  }) => void;
  facilityOptions: { label: string; value: string }[];
  isEdit?: boolean;
  defaultOrderTypeData?: any;
}

export default function OrderTypeDialog({
  isOpen,
  handleClose,
  handleData,
  facilityOptions,
  isEdit = false,
  defaultOrderTypeData,
}: OrderTypeDialogProps) {
  const dialogHeading = isEdit ? "Edit Order Type" : "Add Order Type";
  const defaultFacilityIds: string[] = (
    defaultOrderTypeData?.facilities || []
  )
    .map((f: any) => f?.facility?.id)
    .filter(Boolean);

  const validationSchema = Yup.object().shape({
    [FIELD.NAME]: Yup.string()
      .trim()
      .max(50, "Order type name should not exceed 50 characters")
      .required("Order type name is required"),
    [FIELD.FACILITY_IDS]: Yup.array()
      .of(Yup.string())
      .min(1, "Please select at least one facility")
      .required("Please select at least one facility"),
  });

  return (
    <BootstrapDialog
      onClose={(event, reason) => {
        if (reason !== "backdropClick") {
          handleClose();
        }
      }}
      aria-labelledby="add-order-type-title"
      open={isOpen}
    >
      <Formik
        initialValues={{
          [FIELD.NAME]: defaultOrderTypeData?.name || "",
          [FIELD.IS_ITEM_REQUIRED]: !!defaultOrderTypeData?.isItemRequired,
          [FIELD.IS_NOTE_REQUIRED]: !!defaultOrderTypeData?.isNoteRequired,
          [FIELD.FACILITY_IDS]: defaultFacilityIds,
        }}
        enableReinitialize
        validationSchema={validationSchema}
        onSubmit={(values, { setSubmitting, resetForm }) => {
          try {
            handleData({
              name: (values[FIELD.NAME] as string).trim(),
              isItemRequired: !!values[FIELD.IS_ITEM_REQUIRED],
              isNoteRequired: !!values[FIELD.IS_NOTE_REQUIRED],
              facilityIds: values[FIELD.FACILITY_IDS] as string[],
            });
            resetForm();
          } catch (error) {
            console.log(error);
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          errors,
          values,
          touched,
          setFieldValue,
          handleBlur,
          handleSubmit,
        }) => {
          return (
            <Form onSubmit={handleSubmit}>
              <DialogTitle
                sx={{
                  m: 0,
                  p: 2,
                  fontWeight: 600,
                  fontSize: "18px",
                  fontFamily: "inter",
                }}
                id="add-order-type-title"
              >
                {dialogHeading}
              </DialogTitle>
              <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                  position: "absolute",
                  right: 8,
                  top: 8,
                  color: theme.palette.grey[500],
                })}
              >
                <CloseIcon />
              </IconButton>
              <hr />
              <DialogContent>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: "16px",
                  }}
                >
                  <div className="pt-4">
                    <Input
                      textFieldLabel="Order Type Name"
                      size="small"
                      name={FIELD.NAME}
                      value={values[FIELD.NAME] as string}
                      maxLength={50}
                      onChangeCb={(e) => {
                        setFieldValue(FIELD.NAME, e.target.value);
                      }}
                      onBlur={handleBlur}
                      error={touched[FIELD.NAME] && Boolean(errors[FIELD.NAME])}
                      helperText={
                        touched[FIELD.NAME]
                          ? (errors[FIELD.NAME] as string | undefined)
                          : undefined
                      }
                    />
                  </div>
                  <div>
                    <MultiSelect
                      label="Facilities"
                      name={FIELD.FACILITY_IDS}
                      value={values[FIELD.FACILITY_IDS] as string[]}
                      options={facilityOptions}
                      placeholder="Select facilities..."
                      onChangeCb={(e) => {
                        const selected = Array.isArray(e?.target?.value)
                          ? e.target.value
                          : [e?.target?.value];
                        setFieldValue(FIELD.FACILITY_IDS, selected);
                      }}
                      onBlur={handleBlur}
                      error={
                        touched[FIELD.FACILITY_IDS] &&
                        Boolean(errors[FIELD.FACILITY_IDS])
                      }
                      helperText={
                        touched[FIELD.FACILITY_IDS]
                          ? (errors[FIELD.FACILITY_IDS] as string | undefined)
                          : undefined
                      }
                    />
                  </div>
                  <div className="flex items-center gap-6">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!values[FIELD.IS_ITEM_REQUIRED]}
                          onChange={(e) =>
                            setFieldValue(
                              FIELD.IS_ITEM_REQUIRED,
                              e.target.checked,
                            )
                          }
                          sx={{
                            color: "#7e7c7c",
                            "&.Mui-checked": { color: "#005596" },
                          }}
                        />
                      }
                      label="Item Required"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!values[FIELD.IS_NOTE_REQUIRED]}
                          onChange={(e) =>
                            setFieldValue(
                              FIELD.IS_NOTE_REQUIRED,
                              e.target.checked,
                            )
                          }
                          sx={{
                            color: "#7e7c7c",
                            "&.Mui-checked": { color: "#005596" },
                          }}
                        />
                      }
                      label="Note Required"
                    />
                  </div>
                </div>
              </DialogContent>
              <DialogActions style={{ padding: "24px" }}>
                <Button
                  label={TEXT_CONSTANTS?.CANCEL}
                  variant="outlined"
                  onClickCb={handleClose}
                  style={{
                    width: "120px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                    backgroundColor: "white",
                    border: "none",
                    color: "#393939",
                  }}
                />
                <Button
                  type="submit"
                  label={TEXT_CONSTANTS?.SAVE}
                  variant="contained"
                  onClickCb={() => {}}
                  style={{
                    width: "200px",
                    height: "44px",
                    padding: "12px 20px",
                    fontSize: "16px",
                  }}
                />
              </DialogActions>
            </Form>
          );
        }}
      </Formik>
    </BootstrapDialog>
  );
}
